import { type JSX, Show, createMemo } from 'solid-js';
import type { GetConfigurationResponse } from '@data-access/connect/tc-issue-management';
import {
  resolveTypeIconDisplay,
  getTypeTranslation,
  type IssueIcon,
} from '../../utils/issue-icons';

export interface IssueTypeBadgeProps {
  type?: string;
  configuration?: GetConfigurationResponse | null;
  iconsList?: readonly IssueIcon[];
  /**
   * Optional font-icon class to use as the final fallback when neither an
   * image nor a known font-icon class can be resolved. Defaults to
   * `tc-icon-bcf` to match legacy tcweb behavior.
   */
  fallbackFontIcon?: string;
}

/**
 * Renders a "hollow pill" style badge for an issue type, with a
 * configuration-driven icon (image or font icon) and a translated label.
 *
 * Matches the legacy tcweb Topics list behavior:
 *   - Base64 `iconContent` from the Issue API configuration when available.
 *   - Font-icon fallback for well-known icon class ids or when no icon is
 *     resolved.
 *   - Translated label for well-known type names, raw value otherwise.
 */
export function IssueTypeBadge(props: IssueTypeBadgeProps): JSX.Element {
  const resolved = createMemo(() =>
    resolveTypeIconDisplay(
      props.configuration ?? null,
      props.type,
      props.iconsList,
      props.fallbackFontIcon ?? 'tc-icon-bcf'
    )
  );

  const imageSrc = (): string | undefined => {
    const r = resolved();
    return r.kind === 'image' ? r.src : undefined;
  };

  const fontClassName = (): string => {
    const r = resolved();
    return r.kind === 'font'
      ? r.className
      : (props.fallbackFontIcon ?? 'tc-icon-bcf');
  };

  const label = (): string => getTypeTranslation(props.type);

  return (
    <Show when={props.type}>
      <div class="flex items-center">
        <div class="flex items-center gap-2 py-1 rounded-sm">
          <Show
            when={imageSrc()}
            fallback={<i class={`icon-font ${fontClassName()} i16`} />}
          >
            {(src) => (
              <img class="type-icon-dimension i16" src={src()} alt={label()} />
            )}
          </Show>
          <span class="text-xs font-semibold text-gray-600">{label()}</span>
        </div>
      </div>
    </Show>
  );
}
